import React, { useState } from 'react';
import { connect } from "react-redux";
import { useIntl } from 'react-intl';
import { Switch, Select } from 'components/Form';
import coursesApiClient from 'utils/coursesApiClient';
import { useParams } from 'react-router-dom';
import useQueryApiClient from 'utils/useApiClient';
import { Table } from 'ui';
import useHandleError from 'utils/useHandleError';
import DefaultLayout from '../../DefaultLayout';
import { useLocaleContext } from 'context/LocaleContext';
import { Select as AntDSelect } from 'antd';

interface NotificationsInterface {
    program: any;
    launchButton: HTMLElement;
    session?: any;
}


const mapStateToProps = (state: any) => {
    return {
        session: state.session
    }
};

const Notifications: React.FC<NotificationsInterface> = ({ launchButton, program, session }) => {
    const { locale } = useLocaleContext();

    const [reload, setReload] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [scenarioUpdate, setScenarioUpdate] = useState(false);
    const [dataSource, setDataSource] = useState<any>([]);
    const [templates, setTemplates] = useState([]);

    const params = useParams();
    const intl = useIntl();
    const programId = params.id;
    const [handleError] = useHandleError();

    const handleSwitch = async (record: any, type: 'status' | 'cc') => {
        if (!(!!program?.visualPermissions?.admin || !!program?.visualPermissions?.edit)) {
            return;
        }
        let key: any = Object.entries(dataSource).filter(
            (el: any) => el[1].scenarioId === record.scenarioId
        );
        try {
            let data;
            let url = '';

            switch (type) {
                case 'cc':
                    data = !record.cc;
                    dataSource[key[0][0]].cc = data;
                    setDataSource(dataSource);

                    url = '/set-cc';
                    break;
                case 'status':
                    data = record.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
                    dataSource[key[0][0]].status = data;
                    setDataSource(dataSource);

                    url = '/set-status';
                    break;
                default:
                    return;
            }

            setScenarioUpdate(true);

            await coursesApiClient.request(
                `/api/v1/mail-scenarios/${record.scenarioId}${url}`,
                { [type]: data },
                'POST'
            );

            setScenarioUpdate(false);
        } catch (error) {
            handleError(error);
        }
    };

    const getTemplateUrl = () => {
        let url: string = '';
        switch (session.organization.organization_type) {
            case 'ORGANIZATION':
            case 'RESELLER':
            case 'DISTRIBUTOR':
                url = `/api/v1/mail-templates/organization/${session.organization.organization_id}/select/${6}`
                break;
            case 'CUSTOMER':
                url = `/api/v1/mail-templates/customer/${session.organization.organization_id}/select/${6}`
                break;
        }

        return url;
    }

    useQueryApiClient({
        request: {
            url: getTemplateUrl()
        },
        onSuccess: (response): void => {
            setTemplates(response.mailTemplates);
        },
    });

    const { appendData } = useQueryApiClient({
        request: {
            url: '/api/v1/mail-scenarios/:id/replacement',
            method: 'POST',
        },
        onSuccess: (response): void => {},
    });

    const columns = [
        {
            title: intl.formatMessage({ id: 'emailing.templates.template_name' }),
            render: (text: string, record: any) => {
                const translations = record.translations;
                let titleObject = translations.filter((el: any) => el.language === locale)[0];

                if (!titleObject) {
                    titleObject = translations[Object.keys(translations)[0]];
                }
                return <div>{titleObject.title}</div>;
            },
        },
        {
            title: intl.formatMessage({ id: 'emailing.templates.template_name_replace' }),
            render: (text: string, record: any) => {
                return (
                    <AntDSelect
                        allowClear
                        onChange={(el) => {
                            appendData({ replacement_id: el ?? null }, { id: record.scenarioId });
                        }}
                        showArrow
                        className="default-select w-300"
                        defaultValue={record.replaceTemplateId}
                    >
                        {templates.map((template: any) => {
                            const translations = template.translations;
                            let titleObject = translations.filter(
                                (el: any) => el.language === locale
                            )[0];

                            if (!titleObject) {
                                titleObject = translations[Object.keys(translations)[0]];
                            }
                            return (
                                <AntDSelect.Option value={parseInt(template.id)}>
                                    {titleObject.title}
                                </AntDSelect.Option>
                            );
                        })}
                    </AntDSelect>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'general.status' }),
            render: (_text: string, record: any) => {
                if (!(!!program?.visualPermissions?.admin || !!program?.visualPermissions?.edit)) {
                    return <Switch checked={record.status === 'ACTIVE'} />;
                }

                return (
                    <Switch
                        onChange={() => handleSwitch(record, 'status')}
                        defaultChecked={record.status === 'ACTIVE'}
                    />
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'general.cc' }),
            render: (_text: string, record: any) => {
                if (!(!!program?.visualPermissions?.admin || !!program?.visualPermissions?.edit)) {
                    return <Switch checked={!!record.cc} />;
                }

                return (
                    <Switch
                        onChange={() => handleSwitch(record, 'cc')}
                        defaultChecked={!!record.cc}
                    />
                );
            },
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                url={`/api/v1/learning-program/${programId}/scenarios`}
                setReload={setReload}
                reload={reload}
                manualDataUpdate={scenarioUpdate}
                updateData={dataSource}
                saveData={setDataSource}
                rowSelection={false}
                rowKey={'id'}
                isLoading={isLoading}
            />
            <DefaultLayout.PageFooter right={<>{launchButton}</>} />
        </>
    );
};

export default connect(mapStateToProps, )(Notifications);
